/**
 * 공지사항 관리 페이지
 */
import React, { useState, useMemo, useEffect } from 'react';
import { useNoticeData } from '../../hooks/useNoticeData';
import { useNoticeFilter } from '../../hooks/useNoticeFilter';
import { Notice, CreateNoticeRequestDto, UpdateNoticeRequestDto, TARGET_TYPE_OPTIONS, TargetType } from '../../types/notice.types';
import { NoticeTable } from './NoticeTable';
import { NoticeModal } from '../modals/NoticeModal';
import DateRangePicker from '@/shared/components/forms/DateRangePicker';
import SearchableSelect, { SelectOption } from '@/shared/components/forms/SearchableSelect';
import { usePagination } from "@/shared/hooks/usePagination";
import '@/shared/styles/search-form.css';
import '../../styles/NoticeManagementPage.css';

/** 페이지당 공지사항 수 */
const NOTICE_ITEMS_PER_PAGE = 10;

export const NoticeManagementPage: React.FC = () => {
    const { allNotices, loading, createNotice, updateNotice, deleteNotice } = useNoticeData();
    const { notices, filter, handleFilterChange, handleReset } = useNoticeFilter(allNotices);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedNotice, setSelectedNotice] = useState<Notice | null>(null);

    // 페이지네이션
    const {
        currentPage,
        totalPages,
        currentItems,
        goToPage,
        resetPagination
    } = usePagination({
        items: notices,
        itemsPerPage: NOTICE_ITEMS_PER_PAGE
    });

    // 필터 변경 시 페이지 초기화
    useEffect(() => {
        resetPagination();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    // 대상 타입 옵션을 SelectOption 형식으로 변환
    const targetTypeOptions: SelectOption[] = useMemo(() => {
        return TARGET_TYPE_OPTIONS.map(option => ({
            value: option.value,
            label: option.label,
        }));
    }, []);

    // 등록 버튼 클릭
    const handleCreate = () => {
        setSelectedNotice(null);
        setIsModalOpen(true);
    };

    // 수정 버튼 클릭
    const handleEdit = (notice: Notice) => {
        setSelectedNotice(notice);
        setIsModalOpen(true);
    };

    // 삭제 버튼 클릭
    const handleDelete = async (noticeId: number) => {
        if (window.confirm('정말 삭제하시겠습니까?')) {
            await deleteNotice(noticeId);
        }
    };

    // 모달 저장
    const handleSave = async (data: CreateNoticeRequestDto | UpdateNoticeRequestDto) => {
        let success = false;
        if (selectedNotice) {
            success = await updateNotice(data as UpdateNoticeRequestDto);
        } else {
            success = await createNotice(data as CreateNoticeRequestDto);
        }
        if (success) {
            setIsModalOpen(false);
            setSelectedNotice(null);
        }
    };

    // 모달 닫기
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedNotice(null);
    };

    return (
        <div className="notice-management-page">
            <div className="page-header">
                <h2>공지사항 관리</h2>
            </div>

            {/* 필터 섹션 */}
            <div className="card search-card">
                <div className="card-body">
                    <div className="search-form notice-search-form">
                        {/* 게시기간 (조회기간) */}
                        <div className="form-group date-range-group">
                            <div className="form-field">
                                <label className="form-label">
                                    <i className="fas fa-calendar-alt" />
                                    게시기간
                                </label>
                                <DateRangePicker
                                    startDate={filter.startDate}
                                    endDate={filter.endDate}
                                    onStartDateChange={(value) => handleFilterChange({ startDate: value })}
                                    onEndDateChange={(value) => handleFilterChange({ endDate: value })}
                                    startLabel="시작일"
                                    endLabel="종료일"
                                />
                            </div>
                        </div>

                        {/* 대상 */}
                        <div className="form-group">
                            <div className="form-field">
                                <label className="form-label">
                                    <i className="fas fa-users" />
                                    대상
                                </label>
                                <SearchableSelect
                                    options={targetTypeOptions}
                                    value={filter.targetType}
                                    onChange={(value) => handleFilterChange({ targetType: value as TargetType | '' })}
                                    placeholder="대상 선택..."
                                    showAllOption={true}
                                    allOptionLabel="전체"
                                />
                            </div>
                        </div>

                        {/* 버튼 그룹 */}
                        <div className="form-group button-group">
                            <button className="btn btn-secondary" onClick={handleReset}>
                                <i className="fas fa-redo" />
                                초기화
                            </button>

                            <button className="btn btn-primary" onClick={handleCreate}>
                                공지 등록
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-content">
                <NoticeTable
                    totalCount={notices.length}
                    itemsPerPage={NOTICE_ITEMS_PER_PAGE}
                    currentItems={currentItems}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    loading={loading}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onPageChange={goToPage}
                />
            </div>

            {isModalOpen && (
                <NoticeModal
                    notice={selectedNotice}
                    onSave={handleSave}
                    onClose={handleCloseModal}
                    loading={loading}
                />
            )}
        </div>
    );
};

export default NoticeManagementPage;
